/**
 * API 에러 메시지 i18n 단일 출처
 *
 * client.ts ↔ errors.ts 양쪽이 같은 표를 참조하기 위해 분리.
 * AppContext 가 setApiErrorLanguage 로 현재 로케일을 동기화.
 *
 * #522 — Locale 단일 출처 정합 (15언어 전수). 누락 시 컴파일 에러.
 */
import type { Language, Locale } from '@/config/locales';

type StatusKey = 0 | 400 | 401 | 403 | 404 | 408 | 500 | 502 | 503 | 504;

// #522 — Language(3) + 나머지 Locale(12) 전수 강제 교차 타입
type LocalizedStatusMessages =
  & Record<Language, Record<StatusKey, string>>
  & Record<Exclude<Locale, Language>, Record<StatusKey, string>>;

type LocalizedMessage =
  & Record<Language, string>
  & Record<Exclude<Locale, Language>, string>;

const ERROR_MESSAGES: LocalizedStatusMessages = {
  ko: {
    0: '네트워크 연결을 확인해주세요',
    400: '잘못된 요청입니다',
    401: '인증이 필요합니다',
    403: '접근 권한이 없습니다',
    404: '요청한 리소스를 찾을 수 없습니다',
    408: '요청 시간이 초과되었습니다',
    500: '서버 오류가 발생했습니다',
    502: '서버가 일시적으로 응답할 수 없습니다',
    503: '서버가 일시적으로 응답할 수 없습니다',
    504: '서버가 일시적으로 응답할 수 없습니다',
  },
  es: {
    0: 'Por favor verifica tu conexión de red',
    400: 'Solicitud no válida',
    401: 'Se requiere autenticación',
    403: 'No tienes permiso para acceder',
    404: 'No se encontró el recurso solicitado',
    408: 'Tiempo de espera agotado',
    500: 'Error del servidor',
    502: 'El servidor no responde temporalmente',
    503: 'El servidor no responde temporalmente',
    504: 'El servidor no responde temporalmente',
  },
  en: {
    0: 'Please check your network connection',
    400: 'Invalid request',
    401: 'Authentication required',
    403: 'You do not have permission',
    404: 'The requested resource was not found',
    408: 'Request timed out',
    500: 'Server error',
    502: 'The server is temporarily unresponsive',
    503: 'The server is temporarily unresponsive',
    504: 'The server is temporarily unresponsive',
  },
  pt: {
    0: 'Verifique sua conexão de rede',
    400: 'Solicitação inválida',
    401: 'Autenticação necessária',
    403: 'Você não tem permissão de acesso',
    404: 'O recurso solicitado não foi encontrado',
    408: 'Tempo da solicitação esgotado',
    500: 'Erro no servidor',
    502: 'O servidor está temporariamente indisponível',
    503: 'O servidor está temporariamente indisponível',
    504: 'O servidor está temporariamente indisponível',
  },
  fr: {
    0: 'Veuillez vérifier votre connexion réseau',
    400: 'Requête invalide',
    401: 'Authentification requise',
    403: "Vous n'avez pas l'autorisation d'accéder",
    404: 'La ressource demandée est introuvable',
    408: 'Délai de la requête dépassé',
    500: 'Erreur du serveur',
    502: 'Le serveur est temporairement indisponible',
    503: 'Le serveur est temporairement indisponible',
    504: 'Le serveur est temporairement indisponible',
  },
  zh: {
    0: '请检查您的网络连接',
    400: '无效的请求',
    401: '需要身份验证',
    403: '您没有访问权限',
    404: '未找到请求的资源',
    408: '请求超时',
    500: '服务器错误',
    502: '服务器暂时无法响应',
    503: '服务器暂时无法响应',
    504: '服务器暂时无法响应',
  },
  ja: {
    0: 'ネットワーク接続をご確認ください',
    400: '不正なリクエストです',
    401: '認証が必要です',
    403: 'アクセス権限がありません',
    404: 'リクエストされたリソースが見つかりません',
    408: 'リクエストがタイムアウトしました',
    500: 'サーバーエラーが発生しました',
    502: 'サーバーが一時的に応答できません',
    503: 'サーバーが一時的に応答できません',
    504: 'サーバーが一時的に応答できません',
  },
  hi: {
    0: 'कृपया अपना नेटवर्क कनेक्शन जांचें',
    400: 'अमान्य अनुरोध',
    401: 'प्रमाणीकरण आवश्यक है',
    403: 'आपके पास पहुँच की अनुमति नहीं है',
    404: 'अनुरोधित संसाधन नहीं मिला',
    408: 'अनुरोध का समय समाप्त हो गया',
    500: 'सर्वर त्रुटि हुई',
    502: 'सर्वर अस्थायी रूप से प्रतिक्रिया नहीं दे रहा है',
    503: 'सर्वर अस्थायी रूप से प्रतिक्रिया नहीं दे रहा है',
    504: 'सर्वर अस्थायी रूप से प्रतिक्रिया नहीं दे रहा है',
  },
  de: {
    0: 'Bitte überprüfen Sie Ihre Netzwerkverbindung',
    400: 'Ungültige Anfrage',
    401: 'Authentifizierung erforderlich',
    403: 'Sie haben keine Zugriffsberechtigung',
    404: 'Die angeforderte Ressource wurde nicht gefunden',
    408: 'Zeitüberschreitung der Anfrage',
    500: 'Serverfehler',
    502: 'Der Server ist vorübergehend nicht erreichbar',
    503: 'Der Server ist vorübergehend nicht erreichbar',
    504: 'Der Server ist vorübergehend nicht erreichbar',
  },
  it: {
    0: 'Verifica la tua connessione di rete',
    400: 'Richiesta non valida',
    401: 'Autenticazione richiesta',
    403: 'Non hai i permessi di accesso',
    404: 'La risorsa richiesta non è stata trovata',
    408: 'Tempo della richiesta scaduto',
    500: 'Errore del server',
    502: 'Il server è temporaneamente non disponibile',
    503: 'Il server è temporaneamente non disponibile',
    504: 'Il server è temporaneamente non disponibile',
  },
  ru: {
    0: 'Проверьте подключение к сети',
    400: 'Неверный запрос',
    401: 'Требуется аутентификация',
    403: 'У вас нет прав доступа',
    404: 'Запрошенный ресурс не найден',
    408: 'Время запроса истекло',
    500: 'Ошибка сервера',
    502: 'Сервер временно не отвечает',
    503: 'Сервер временно не отвечает',
    504: 'Сервер временно не отвечает',
  },
  ar: {
    0: 'يرجى التحقق من اتصالك بالشبكة',
    400: 'طلب غير صالح',
    401: 'المصادقة مطلوبة',
    403: 'ليس لديك إذن بالوصول',
    404: 'لم يتم العثور على المورد المطلوب',
    408: 'انتهت مهلة الطلب',
    500: 'حدث خطأ في الخادم',
    502: 'الخادم غير قادر على الاستجابة مؤقتًا',
    503: 'الخادم غير قادر على الاستجابة مؤقتًا',
    504: 'الخادم غير قادر على الاستجابة مؤقتًا',
  },
  vi: {
    0: 'Vui lòng kiểm tra kết nối mạng',
    400: 'Yêu cầu không hợp lệ',
    401: 'Cần xác thực',
    403: 'Bạn không có quyền truy cập',
    404: 'Không tìm thấy tài nguyên được yêu cầu',
    408: 'Yêu cầu đã hết thời gian chờ',
    500: 'Đã xảy ra lỗi máy chủ',
    502: 'Máy chủ tạm thời không phản hồi',
    503: 'Máy chủ tạm thời không phản hồi',
    504: 'Máy chủ tạm thời không phản hồi',
  },
  th: {
    0: 'กรุณาตรวจสอบการเชื่อมต่อเครือข่ายของคุณ',
    400: 'คำขอไม่ถูกต้อง',
    401: 'ต้องมีการยืนยันตัวตน',
    403: 'คุณไม่มีสิทธิ์เข้าถึง',
    404: 'ไม่พบทรัพยากรที่ร้องขอ',
    408: 'คำขอหมดเวลา',
    500: 'เกิดข้อผิดพลาดของเซิร์ฟเวอร์',
    502: 'เซิร์ฟเวอร์ไม่สามารถตอบสนองได้ชั่วคราว',
    503: 'เซิร์ฟเวอร์ไม่สามารถตอบสนองได้ชั่วคราว',
    504: 'เซิร์ฟเวอร์ไม่สามารถตอบสนองได้ชั่วคราว',
  },
  nl: {
    0: 'Controleer uw netwerkverbinding',
    400: 'Ongeldig verzoek',
    401: 'Authenticatie vereist',
    403: 'U heeft geen toegangsrechten',
    404: 'De gevraagde bron is niet gevonden',
    408: 'Time-out van het verzoek',
    500: 'Serverfout',
    502: 'De server is tijdelijk niet beschikbaar',
    503: 'De server is tijdelijk niet beschikbaar',
    504: 'De server is tijdelijk niet beschikbaar',
  },
};

const PARSE_FALLBACK: LocalizedMessage = {
  ko: '서버 응답을 파싱할 수 없습니다',
  es: 'No se pudo procesar la respuesta del servidor',
  en: 'Could not parse the server response',
  pt: 'Não foi possível processar a resposta do servidor',
  fr: "Impossible d'analyser la réponse du serveur",
  zh: '无法解析服务器响应',
  ja: 'サーバーの応答を解析できません',
  hi: 'सर्वर प्रतिक्रिया को पार्स नहीं किया जा सका',
  de: 'Die Serverantwort konnte nicht verarbeitet werden',
  it: 'Impossibile elaborare la risposta del server',
  ru: 'Не удалось обработать ответ сервера',
  ar: 'تعذر تحليل استجابة الخادم',
  vi: 'Không thể phân tích phản hồi từ máy chủ',
  th: 'ไม่สามารถประมวลผลการตอบกลับของเซิร์ฟเวอร์ได้',
  nl: 'Kon het serverantwoord niet verwerken',
};

const GENERIC_FALLBACK: LocalizedMessage = {
  ko: '요청 처리 중 오류가 발생했습니다',
  es: 'Ocurrió un error al procesar la solicitud',
  en: 'An error occurred while processing the request',
  pt: 'Ocorreu um erro ao processar a solicitação',
  fr: 'Une erreur est survenue lors du traitement de la demande',
  zh: '处理请求时发生错误',
  ja: 'リクエストの処理中にエラーが発生しました',
  hi: 'अनुरोध संसाधित करते समय त्रुटि हुई',
  de: 'Bei der Verarbeitung der Anfrage ist ein Fehler aufgetreten',
  it: "Si è verificato un errore durante l'elaborazione della richiesta",
  ru: 'Произошла ошибка при обработке запроса',
  ar: 'حدث خطأ أثناء معالجة الطلب',
  vi: 'Đã xảy ra lỗi khi xử lý yêu cầu',
  th: 'เกิดข้อผิดพลาดขณะประมวลผลคำขอ',
  nl: 'Er is een fout opgetreden bij het verwerken van het verzoek',
};

// #522 — Language(3) → Locale(15) 확장. 기존 호출처(Language 전달)와 하위 호환.
let currentLocale: Locale = 'es';

export function setApiErrorLanguage(lang: Locale): void {
  currentLocale = lang;
}

export function getApiErrorLanguage(): Locale {
  return currentLocale;
}

/**
 * 상태 코드를 현재 로케일 메시지로 변환. 매핑 없으면 undefined.
 */
export function getStatusMessage(status: number): string | undefined {
  const messages = ERROR_MESSAGES[currentLocale] ?? ERROR_MESSAGES.es;
  return messages[status as StatusKey];
}

/**
 * "응답 파싱 실패" 메시지 (서버 응답이 JSON 이 아닐 때).
 */
export function getParseFallbackMessage(): string {
  return PARSE_FALLBACK[currentLocale] ?? PARSE_FALLBACK.es;
}

/**
 * 상태 코드 매핑이 없을 때 사용할 기본 에러 메시지.
 */
export function getGenericFallbackMessage(): string {
  return GENERIC_FALLBACK[currentLocale] ?? GENERIC_FALLBACK.es;
}
